import React, { useEffect, useRef } from "react"
import { useField, useFormikContext } from "formik"
import { enqueueSnackbar } from "notistack"

import { ApiQueryAutocomplete } from "../../../../../core/api/components/ApiQueryAutocomplete"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { ConfigQuery, fetchDimensionDetailsForUser } from "../../createOrderApi"
import { SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { useAppLocalization } from "../../../../../modules/localization/components/AppLocalizationProvider"
import { StandardErrorDialog, useStandardErrorDialogState } from "../../../dialogs/StandardErrorDialog"

import { getFormikFieldError, isAccountingDimensionKey } from "./helpers"
import { ExtendedDynamicConfigKey, DynamicConfigField } from "./formTypes"
import { handleAutoCompleteMode, useAutocompleteController } from "./useAutocompleteController"
import { useFormResetWatcher } from "./useFormResetWatcher"

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null
}

function getProp(obj: unknown, key: string): unknown {
    return isRecord(obj) ? obj[key] : undefined
}

function isCostCenter(value: unknown): value is { value: string; name: string } {
    if (!isRecord(value)) return false
    return typeof value["value"] === "string" && typeof value["name"] === "string"
}

function transformData(data: unknown): Array<{ id: string; name: string }> {
    const costCenters = getProp(getProp(data, "accountDimension"), "costCenters")
    if (!Array.isArray(costCenters)) return []

    return costCenters.filter(isCostCenter).map(({ value, name }) => ({ id: value, name }))
}

export type AccountingDimensionOption = Readonly<{
    id: string
    name: string
    dimensionName?: string
}>

type DimensionDetailsQueryParams = Readonly<{
    userId: number
    dimensionId: number
    search: string
    forceRefreshKey: number
}>

function fetchDimensionDetailsForUserWithRefreshKey(
    { forceRefreshKey: _forceRefreshKey, ...params }: DimensionDetailsQueryParams,
    axios: Parameters<typeof fetchDimensionDetailsForUser>[1],
    signal?: Parameters<typeof fetchDimensionDetailsForUser>[2],
) {
    return fetchDimensionDetailsForUser(params, axios, signal)
}

type DynamicAutocompleteProps = {
    config: DynamicConfigField
    user?: SelectedMainUserState
}

export function DynamicAutocomplete({ config, user }: DynamicAutocompleteProps) {
    if (!user) return <></>
    const { l10n } = useAppLocalization()
    const { key, inputField, label, description } = config

    const [field, meta, helpers] = useField<AccountingDimensionOption | null>(key)
    const { submitCount } = useFormikContext<unknown>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)
    const wasReset = useFormResetWatcher(key)

    const mode = handleAutoCompleteMode(inputField?.mode)

    const {
        debouncedSearch,
        enabled,
        isLoadOnOpen,
        forceRefreshKey,
        handleInputChange,
        handleOpen,
        handleClose,
        handleClearSearch,
    } = useAutocompleteController(mode, wasReset)

    function getAutocompleteQueryConfig(key: ExtendedDynamicConfigKey, user: SelectedMainUserState, search: string) {
        if (isAccountingDimensionKey(key)) {
            const dimensionIdFromKey = Number(key.split("_")[2])
            return {
                fetchFn: fetchDimensionDetailsForUserWithRefreshKey,
                queryName: ConfigQuery.ACCOUNT_DIMENSION_DETAILS_FOR_USER,
                dependencies: {
                    userId: user.userId,
                    dimensionId: dimensionIdFromKey,
                    search,
                },
                select: transformData,
            }
        }

        return null
    }

    const queryConfig = getAutocompleteQueryConfig(key, user, debouncedSearch)
    if (!queryConfig) return <></>

    const {
        data: options = [],
        isError,
        isLoading,
        refetch,
    } = useApiQuery<DimensionDetailsQueryParams, unknown, AccountingDimensionOption[]>(queryConfig.fetchFn, {
        queryName: queryConfig.queryName,
        dependencies: {
            ...queryConfig.dependencies,
            forceRefreshKey,
        },
        select: queryConfig.select,
        enabled,
    })

    const dimensionName = label ?? description ?? ""
    const withDimensionName = (value: AccountingDimensionOption | null): AccountingDimensionOption | null =>
        value ? { ...value, dimensionName } : value

    const notifiedAboutMissingCostCentersRef = useRef(false)
    const { errorDialog, closeErrorDialog, openErrorDialog } = useStandardErrorDialogState()

    useEffect(() => {
        // Always fetch once on mount (regardless of mode from config).
        // After that, all query behavior is controlled by `enabled` from useAutocompleteController.
        if (!field.value) void refetch()
    }, [])

    useEffect(() => {
        // If API returns no cost centers (or unexpected payload shape), don't show generic "Unknown error".
        // Instead show a clear message: modal when required, snackbar when optional.
        if (notifiedAboutMissingCostCentersRef.current) return
        if (!enabled || isLoading || isError) return
        if (field.value) return
        if (debouncedSearch.trim() !== "") return
        if (options.length !== 0) return

        notifiedAboutMissingCostCentersRef.current = true
        const message = l10n.getString("create-order-cost-centers-missing-message", { dimensionName })

        if (inputField?.isRequired) {
            openErrorDialog({
                title: l10n.getString("create-order-cost-centers-missing-dialog-title"),
                description: message,
            })
        } else {
            enqueueSnackbar({ variant: "warning", message })
        }
    }, [
        debouncedSearch,
        dimensionName,
        enabled,
        field.value,
        inputField?.isRequired,
        isError,
        isLoading,
        l10n,
        openErrorDialog,
        options,
    ])

    useEffect(() => {
        // Auto-fill when there is exactly one option and the field is empty.
        // Important: only do this for the initial (non-search) result set, so we don't auto-select
        // a single match while the user is typing and keep the existing query conditions intact.
        if (debouncedSearch.trim() === "" && options.length === 1 && !field.value) {
            helpers.setValue(withDimensionName(options[0]))
        }
    }, [debouncedSearch, field.value, helpers, options])

    useEffect(() => {
        if (inputField?.predefined && !field.value) {
            helpers.setValue(
                withDimensionName({ id: String(inputField.predefined.value), name: inputField.predefined.text || "" }),
            )
        }
    }, [])

    const handleChange = (_event: React.SyntheticEvent, value: AccountingDimensionOption | null) => {
        helpers.setValue(withDimensionName(value))
        if (isLoadOnOpen) {
            handleClearSearch()
        }
    }

    return (
        <>
            <ApiQueryAutocomplete
                isLoading={enabled && isLoading}
                isError={isError}
                options={options}
                getOptionLabel={(option) => option.name || ""}
                value={field.value || null}
                onChange={handleChange}
                onInputChange={handleInputChange}
                onOpen={handleOpen}
                onClose={handleClose}
                size="small"
                slotProps={{
                    textField: formikErrorProps,
                }}
                label={`${label || description}${inputField?.isRequired ? " *" : ""}`}
                isOptionEqualToValue={(option, value) => {
                    if (!value || !option) return false
                    return option.id === value.id
                }}
                filterOptions={(x) => x}
                readOnly={options.length === 1}
            />

            <StandardErrorDialog dialogDetails={errorDialog} onClose={closeErrorDialog} />
        </>
    )
}
